import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiUser, FiLock, FiLogIn, FiAlertCircle } from 'react-icons/fi';
import { Input, Button, Card, WaveBackground } from '../styles/GlobalStyles';
import { useAuth } from '../context/AuthContext';

const LoginSection = styled(WaveBackground)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.gradients.background};
  padding: 2rem 0;
`;

const LoginContainer = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  padding: 0 20px;
`;

const LoginCard = styled(Card)`
  padding: 3rem;
  text-align: center;
  background: white;
  box-shadow: ${props => props.theme.shadows.ocean};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 2rem;
  }
`;

const LoginHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const LoginTitle = styled.h1`
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const LoginSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const InputWithIcon = styled.div`
  position: relative;
  
  .input-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.colors.grayLight};
    z-index: 2;
  }
  
  input {
    padding-left: 3rem;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: ${props => props.theme.colors.error};
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(239, 68, 68, 0.2);
`;

const LoginButton = styled(Button)`
  width: 100%;
  justify-content: center;
  padding: 16px;
  font-size: 1.1rem;
  margin-top: 1rem;
`;

const AdminNote = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.medium};
  border-left: 4px solid ${props => props.theme.colors.primary};
  text-align: left;

  .note-title {
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  .note-text {
    color: ${props => props.theme.colors.textLight};
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const BackToHome = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  margin-top: 2rem;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primaryLight};
  }
`;

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/admin';

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, from]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.password) {
      setError('Bitte füllen Sie alle Felder aus.');
      return;
    }

    const result = await login(formData.username, formData.password);
    
    if (!result.success) {
      setError(result.message);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <LoginSection>
        <LoginContainer>
          <LoginCard>
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              Wird geladen...
            </div>
          </LoginCard>
        </LoginContainer>
      </LoginSection>
    );
  }

  return (
    <LoginSection>
      <LoginContainer>
        <LoginCard>
          <LoginHeader>
            <LoginTitle>
              <FiUser size={40} />
              Admin Login
            </LoginTitle>
            <LoginSubtitle>
              Melden Sie sich an, um auf das Admin-Panel zuzugreifen
            </LoginSubtitle>
          </LoginHeader>

          {error && (
            <ErrorMessage>
              <FiAlertCircle size={20} />
              {error}
            </ErrorMessage>
          )}

          <LoginForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Benutzername</Label>
              <InputWithIcon>
                <FiUser className="input-icon" size={18} />
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Ihr Benutzername"
                  required
                />
              </InputWithIcon>
            </FormGroup>

            <FormGroup>
              <Label>Passwort</Label>
              <InputWithIcon>
                <FiLock className="input-icon" size={18} />
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Ihr Passwort"
                  required
                />
              </InputWithIcon>
            </FormGroup>

            <LoginButton type="submit" disabled={loading}>
              <FiLogIn size={20} />
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </LoginButton>
          </LoginForm>

          <AdminNote>
            <div className="note-title">Hinweis für Administratoren</div>
            <div className="note-text">
              Dieses Login ist nur für autorisierte Administratoren bestimmt. 
              Mit dem Admin-Zugang können Sie Korallen und Fische verwalten, 
              Kontaktanfragen einsehen und Inhalte bearbeiten.
            </div>
          </AdminNote>

          <BackToHome onClick={handleBackToHome}>
            ← Zurück zur Startseite
          </BackToHome>
        </LoginCard>
      </LoginContainer>
    </LoginSection>
  );
};

export default Login;

